import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ToolTag {
  id: string;
  name: string;
  totalViews: number;
  resourceCount: number;
}

export function useToolsFilter(organizationId: string | undefined) {
  const [allTools, setAllTools] = useState<ToolTag[]>([]);
  const [topTools, setTopTools] = useState<ToolTag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organizationId) {
      fetchTools();
    }
  }, [organizationId]);

  const fetchTools = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    try {
      // Use optimized RPC function instead of N+1 queries
      const { data, error } = await supabase
        .rpc('get_tools_with_stats', { p_org_id: organizationId });

      if (error) {
        console.error('Error fetching tools with stats:', error);
        setLoading(false);
        return;
      }

      // Map to our interface
      const toolsWithViews: ToolTag[] = (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        totalViews: Number(row.total_views) || 0,
        resourceCount: Number(row.resource_count) || 0
      }));
      
      setAllTools(toolsWithViews);
      setTopTools(toolsWithViews.slice(0, 5));
    } catch (error) {
      console.error('Error in fetchTools:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchTools = useCallback((query: string): ToolTag[] => {
    if (!query.trim()) return allTools;
    const lowerQuery = query.toLowerCase();
    return allTools.filter(t => t.name.toLowerCase().includes(lowerQuery));
  }, [allTools]);

  const refresh = useCallback(() => {
    if (organizationId) {
      fetchTools();
    }
  }, [organizationId]);

  return { allTools, topTools, loading, searchTools, refresh };
}
